/**
 * Turn a user-agent header into a label somebody recognises as their own device.
 *
 * The sessions table used to print the header verbatim, so every row read
 * "Mozilla/5.0 (Macintosh; Intel Ma…" and the one thing the table exists for,
 * spotting a session you do not recognise, was impossible.
 *
 * Deliberately shallow. This is a label, not analytics: no versions, no
 * attempt to tell a wrapped web view from the browser around it. Anything
 * unrecognised falls back to the raw header rather than guessing, because a
 * confident wrong device name is worse than an ugly true one.
 */

/* Order matters. Every Chromium browser also claims Chrome and Safari, and
   Chrome on iOS claims neither, so the specific tokens have to be tried
   first and the generic ones last. */
const BROWSERS: [RegExp, string][] = [
  [/\bEdg(?:iOS|A)?\//, 'Edge'],
  [/\bOPR\/|\bOpera\//, 'Opera'],
  [/\bSamsungBrowser\//, 'Samsung Internet'],
  [/\bCriOS\//, 'Chrome'],
  [/\bFxiOS\//, 'Firefox'],
  [/\bFirefox\//, 'Firefox'],
  [/\bChrome\//, 'Chrome'],
  [/\bSafari\//, 'Safari'],
];

/* Also order-dependent, and for the same reason: an iPhone says "like Mac OS
   X", Android says "Linux", and a Chromebook says "X11". The narrow match has
   to win. */
const PLATFORMS: [RegExp, string][] = [
  [/\bWindows\b/, 'Windows'],
  [/\biPhone\b/, 'iPhone'],
  [/\biPad\b/, 'iPad'],
  [/\bAndroid\b/, 'Android'],
  [/\bCrOS\b/, 'ChromeOS'],
  [/\bMacintosh\b|\bMac OS X\b/, 'macOS'],
  [/\bX11\b|\bLinux\b/, 'Linux'],
];

function first(pairs: [RegExp, string][], ua: string): string | null {
  for (const [re, name] of pairs) if (re.test(ua)) return name;
  return null;
}

/**
 * "Chrome on macOS", or the nearest honest thing to it.
 *
 * An app's own web view sends no browser token at all, so those come back as
 * the bare platform ("iPhone") rather than being guessed into a browser they
 * are not.
 */
export function describeDevice(ua: string | null | undefined): string {
  const raw = (ua ?? '').trim();
  if (!raw) return 'Unknown device';

  const browser = first(BROWSERS, raw);
  const platform = first(PLATFORMS, raw);

  if (browser && platform) return `${browser} on ${platform}`;
  return browser ?? platform ?? raw;
}
